import React, { useState, useEffect, ChangeEvent } from 'react';
import { Dialog, Button, withStyles, createStyles, WithStyles, Theme } from '@material-ui/core';
import { Tab, Tabs } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import RestaurantView from './View';
import { ITable } from '../../types/IRestaurantTablesStore';
import { TActiveTables } from '../../types/IReserveModalStore';
import { TSectionAdaptSchema } from '../../types/IRestaurantTablesSchemaStore';
import { IViewType } from './ReservesDataFields';
import { IPropsSelectOption } from '../../reducers/selectors';
import locales from '../../locales';
import CircleCheckBox from '../Common/CircleCheckBox';

const StyledTab = withStyles({
  root: {
    minWidth: 80,
    fontSize: '16px',
  },
})(Tab);

const styles = (theme: Theme) => createStyles({
  paper: {
    margin: 0,
    maxWidth: '100vw',
    maxHeight: '100dvh',
    width: '100vw',
    height: '100dvh',
    borderRadius: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    padding: 0,
    boxSizing: 'border-box',
    minHeight: '100dvh',
    position: 'relative', // Для абсолютного позиционирования дочерних элементов
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    // Добавляем отступ сверху для safe-area (браузерные UI элементы)
    paddingTop: theme.spacing(2),
    borderBottom: '1px solid #E5E5E5',
    backgroundColor: '#ffffff',
    zIndex: 10,
    // Фиксируем header сверху
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    flexShrink: 0,
  },
  backButton: {
    marginRight: theme.spacing(2),
    minWidth: 'auto',
    padding: theme.spacing(1),
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: 500,
  },
  sectionSelect: {
    flex: 1,
    marginLeft: theme.spacing(2),
    minWidth: 200,
  },
  tabs: {
    borderBottom: '1px solid #E5E5E5',
    backgroundColor: '#ffffff',
    zIndex: 9,
    flexShrink: 0,
    // Фиксируем tabs под header
    marginTop: '80px',
    top: `calc(56px + ${theme.spacing(2)}px * 2)`, // Высота header (56px) + padding сверху и снизу
    left: 0,
    right: 0,
  },
  viewContainer: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Используем flex: 1 для автоматического расчета высоты
    flex: 1,
    // Отступы сверху и снизу для фиксированных элементов (header, tabs, actions)
    // header: ~56px (высота кнопки + padding), tabs: 48px, actions: ~72px (высота кнопки + padding)
    marginTop: `calc(56px + ${theme.spacing(2)}px * 2+ 48px)`, // header + tabs
    marginBottom: `calc(72px + ${theme.spacing(2)}px * 2)`, // actions
    [theme.breakpoints.down(465)]: {
      // На мобильных actions выше из-за вертикального расположения (~100px)
      marginBottom: `calc(100px + ${theme.spacing(2)}px * 2)`,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    // Добавляем отступ снизу для safe-area (браузерные UI элементы)
    paddingBottom: theme.spacing(2),
    borderTop: '1px solid #E5E5E5',
    backgroundColor: '#ffffff',
    gap: theme.spacing(1.5),
    // Фиксируем кнопки внизу с учетом safe-area
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexShrink: 0,
    [theme.breakpoints.down(465)]: {
      flexDirection: 'column',
      '& > button': {
        width: '100%',
        margin: '0 !important',
      },
    },
  },
  cancelButton: {
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(1.5),
    },
  },
  saveButton: {
    minWidth: 120,
    [theme.breakpoints.up('sm')]: {
      marginLeft: 'auto',
    },
  },
  mergeCheckbox: {
    flex: '0 0 auto',
    marginRight: 'auto',
  },
});

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  activeTables: TActiveTables;
  handleSelectTable: (table: ITable) => void;
  tables: Array<ITable> | void;
  schema: TSectionAdaptSchema | void;
  viewId: IViewType;
  isSchemasEnabled: boolean;
  persons: number;
  isMergeTables: boolean;
  alreadySelectedTables: Array<number>;
  onViewChange: (viewType: IViewType) => void;
  sections: Array<IPropsSelectOption>;
  section: number;
  onSectionChange: (sectionId: number) => void;
  handleMergeTables: () => void;
  onReservePropertyChange: (name: string, value: any) => void;
  disabled?: boolean;
  formValues?: {
    date?: any;
    time?: string;
    persons?: number;
    hours?: string;
    minutes?: string;
  };
}

const TableSelectModal: React.FC<IProps> = ({
  classes,
  open,
  onClose,
  onSave,
  activeTables,
  handleSelectTable,
  tables,
  schema,
  viewId,
  isSchemasEnabled,
  persons,
  isMergeTables,
  alreadySelectedTables,
  onViewChange,
  sections,
  section,
  onSectionChange,
  handleMergeTables,
  onReservePropertyChange,
  disabled,
}) => {
  const [localViewType, setLocalViewType] = useState<IViewType>(viewId);

  // Определяем, является ли устройство мобильным или планшетом для скрытия стрелок
  const isClient = typeof window !== 'undefined';
  const isMobile = isClient && window.innerWidth < 960; // Мобильные и планшеты (< 960px)

  useEffect(() => {
    setLocalViewType(viewId);
  }, [viewId]);

  const handleViewChange = (event: any, value: IViewType) => {
    setLocalViewType(value);
    onViewChange(value);
  };

  const selectedTablesCount = Object.keys(activeTables).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        className: classes.paper,
      }}
    >
      <div className={classes.content}>
        {/* Заголовок с кнопкой назад */}
        <div className={classes.header}>
          <Button
            className={classes.backButton}
            onClick={onClose}
            startIcon={<ArrowBackIcon />}
          >
            Назад
          </Button>
          <FormControl className={classes.sectionSelect}>
            <InputLabel htmlFor="section">{locales.t('common.reserveModal.sections')}</InputLabel>
            <Select
              value={section || ''}
              onChange={(e) => onSectionChange(Number(e.target.value))}
              inputProps={{
                name: 'section',
                id: 'section',
              }}
            >
              <MenuItem value={0}>
                {locales.t('common.reserveModal.allSections')}
              </MenuItem>
              {sections.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Табы схема/список */}
        <Tabs
          className={classes.tabs}
          value={localViewType}
          onChange={handleViewChange}
          scrollButtons="off"
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          centered
        >
          <StyledTab
            label="Список"
            value="table"
          />
          <StyledTab
            label="Схема"
            value="scheme"
          />
        </Tabs>

        {/* Окно выбора столов */}
        <div className={classes.viewContainer}>
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <RestaurantView
              activeTables={activeTables}
              isMergeTables={isMergeTables}
              handleSelectTable={handleSelectTable}
              tables={tables}
              schema={schema}
              viewId={localViewType}
              isSchemasEnabled={isSchemasEnabled}
              persons={isMergeTables ? 0 : persons}
              alreadySelectedTables={alreadySelectedTables}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Кнопки действий */}
        <div className={classes.actions}>
          <div className={classes.mergeCheckbox}>
            <CircleCheckBox
              label={locales.t('common.reserveModal.tableMerge')}
              value={isMergeTables}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const { checked } = e.target;
                onReservePropertyChange('isMergeTables', checked);
                // Вызываем handleMergeTables только если поля формы заполнены
                // handleMergeTables сам проверяет validateReserveFields, но мы можем вызвать его всегда
                // так как он безопасно обрабатывает случай, когда поля не заполнены
                if (handleMergeTables) {
                  handleMergeTables();
                }
              }}
              isClickableLabel
              disabled={disabled}
            />
          </div>
          <Button
            className={classes.saveButton}
            variant="contained"
            color="primary"
            onClick={onSave}
            fullWidth
            disabled={selectedTablesCount === 0}
          >
            Сохранить {selectedTablesCount > 0 && `(${selectedTablesCount})`}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default withStyles(styles)(TableSelectModal);
